import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  IconButton,
  useTheme,
  styled,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LanguageSelector from '../LanguageSelector';
import { useTranslation } from 'react-i18next';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import { getCrmSession } from '../../crm/api/crmHttp';
import { PORTAL_NAV, isPortalNavActive } from './portalNav';
import useTenantPortalEntry from './useTenantPortalEntry';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 296,
    maxWidth: '86vw',
    background: theme.palette.background.default,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const NavItem = ({ icon, label, onClick, active, suffix }) => {
  const theme = useTheme();
  return (
    <ListItemButton
      onClick={onClick}
      selected={!!active}
      aria-current={active ? 'page' : undefined}
      sx={{
        borderRadius: 2,
        mx: 1,
        my: 0.25,
        '&.Mui-selected': {
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
          color: 'primary.main',
          '& .MuiListItemIcon-root': { color: 'primary.main' },
          '& .MuiListItemText-primary': { fontWeight: 700 },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText primary={label} />
      {suffix}
    </ListItemButton>
  );
};

const sectionHeaderSx = { bgcolor: 'transparent', lineHeight: '36px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.72rem' };

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const crmSession = open ? getCrmSession() : null;
  // Kiracı paneli girişi: bağlı kira sözleşmesi varsa (çekmece açılınca tembel ve önbellekli kontrol)
  const showTenantPortal = useTenantPortalEntry(currentUser?.id, open);

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const accountItems = [
    { label: t('navigation.myAdvertisements'), icon: <ListAltIcon />, path: '/my-advertisements' },
    { label: t('savedSearch.menu'), icon: <SavedSearchIcon />, path: '/kayitli-aramalar' },
    { label: t('chat.title'), icon: <ChatIcon />, path: '/chat' },
    { label: t('notification.title'), icon: <NotificationsIcon />, path: '/notifications' },
    ...(showTenantPortal ? [{ label: t('tenantPortal.menu'), icon: <KeyOutlinedIcon />, path: '/kiraci' }] : []),
    { label: t('profile.menu.profile'), icon: <PersonOutlineRoundedIcon />, path: '/profile' },
    { label: t('profile.menu.identityVerification'), icon: <VerifiedUserOutlinedIcon />, path: '/profile/identity-verification' },
    { label: t('profile.menu.changePassword'), icon: <LockResetOutlinedIcon />, path: '/profile/change-password' },
  ];

  return (
    <StyledDrawer
      id="portal-drawer"
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ component: 'nav', 'aria-label': t('portal.nav.mainMenu') }}
    >
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="div" sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: -0.3 }}>
            ODAZEN
          </Typography>
          <IconButton onClick={onClose} aria-label={t('sidebar.close')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            component={Link}
            to="/add-roommate"
            onClick={onClose}
            fullWidth
            variant="contained"
            startIcon={<AddRoundedIcon />}
          >
            {t('portal.nav.addListing')}
          </Button>
        </Box>

        <Divider />

        <List subheader={<ListSubheader sx={sectionHeaderSx}>{t('portal.nav.explore')}</ListSubheader>} sx={{ pt: 0.5 }}>
          <NavItem
            icon={<HomeOutlinedIcon />}
            label={t('portal.nav.home')}
            active={location.pathname === '/'}
            onClick={() => handleNavigate('/')}
          />
          {PORTAL_NAV.map(({ key, to, Icon }) => (
            <NavItem
              key={key}
              icon={<Icon />}
              label={t(key === 'agencies' ? 'portal.nav.agenciesLong' : `portal.nav.${key}`)}
              active={isPortalNavActive(key, location)}
              onClick={() => handleNavigate(to)}
            />
          ))}
          <NavItem
            icon={<InfoOutlinedIcon />}
            label={t('portal.nav.about')}
            active={location.pathname === '/tanitim'}
            onClick={() => handleNavigate('/tanitim')}
          />
        </List>

        {currentUser || crmSession ? (
          <>
            <Divider />
            <List subheader={<ListSubheader sx={sectionHeaderSx}>{t('portal.nav.account')}</ListSubheader>} sx={{ pt: 0.5 }}>
              {currentUser
                ? accountItems.map((item) => (
                    <NavItem
                      key={item.path}
                      icon={item.icon}
                      label={item.label}
                      active={location.pathname === item.path}
                      onClick={() => handleNavigate(item.path)}
                    />
                  ))
                : null}
              {crmSession ? (
                <NavItem icon={<DashboardCustomizeOutlinedIcon />} label={t('portal.nav.crmPanel')} onClick={() => handleNavigate('/crm')} />
              ) : null}
              {currentUser ? <NavItem icon={<LogoutRoundedIcon />} label={t('profile.menu.logout')} onClick={handleLogout} /> : null}
            </List>
          </>
        ) : null}

        <Divider />

        <List subheader={<ListSubheader sx={sectionHeaderSx}>{t('sidebar.settings')}</ListSubheader>} sx={{ pt: 0.5 }}>
          <NavItem
            icon={mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            label={t(`theme.${mode === 'dark' ? 'light' : 'dark'}`)}
            onClick={toggleTheme}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.5, color: theme.palette.text.secondary }}>
            <LanguageSelector />
            <Typography sx={{ fontSize: '0.95rem' }}>{t('language.select')}</Typography>
          </Box>
        </List>

        {!currentUser ? (
          <>
            <Divider />
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button component={Link} to="/login" fullWidth variant="outlined" sx={{ mb: 1 }} onClick={onClose}>
                {t('navigation.login')}
              </Button>
              <Button component={Link} to="/register" fullWidth variant="contained" onClick={onClose}>
                {t('navigation.register')}
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
